// external dependencies
import Vue from 'vue';
import VueSlider from 'vue-slider-component';
import vueDebounce, { PluginConfig } from 'vue2-debounce';
import VueContentPlaceholders from 'vue-content-placeholders';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import ELEMENT from 'element-ui';
import VTooltip from 'v-tooltip';
import vuescroll from 'vuescroll';
import { createPinia, PiniaVuePlugin } from 'pinia';
import VueRouter from 'vue-router';
import { abilitiesPlugin } from '@casl/vue';
import ECharts from 'vue-echarts';
import { use } from 'echarts/core';

// import ECharts modules manually to reduce bundle size
import {
  CanvasRenderer,
} from 'echarts/renderers';
import {
  BarChart,
  LineChart,
  PieChart,
} from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';

// @ts-ignore
import * as components from './components/countly/lib-components/index';
import './assets/styles/main.css';
import 'element-ui/lib/theme-chalk/index.css';
import './components/countly/styles/main.scss';
import 'vue-slider-component/theme/default.css';
import './assets/styles/overrides.css';
// internal dependencies
import App from './App.vue';
import router from './router';
import './utils/validators';
import './utils/global-filters';

import i18n from './i18n';
// @ts-ignore
import DialogCommons from './components/countly/lib-components/dialog/commons';
// @ts-ignore
import NotificationCommons from './components/countly/lib-components/notification/commons';
// @ts-ignore
import ScrollShadow from './components/countly/directives/scroll-shadow';
import { ability } from './utils/ability';

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
]);

Vue.directive('scroll-shadow', ScrollShadow);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

if (VTooltip.options && VTooltip.options.popover) {
  VTooltip.options.defaultClass = 'cly-vue-tooltip';
  VTooltip.options.defaultBoundariesElement = 'body';
  VTooltip.options.popover.defaultTrigger = 'hover';
  VTooltip.options.popover.defaultOffset = 14;
  VTooltip.options.popover.defaultBoundariesElement = 'window';
  VTooltip.options.popover.defaultClass = 'cly-vue-popover';
  VTooltip.options.popover.defaultInnerClass = 'cly-vue-popover__content';
}

// install function executed by Vue.use()
Object.entries(components).forEach((item) => {
  const componentName = item[0];
  const component = item[1] as Vue.ComponentOptions<Vue>;
  Vue.component(componentName, component);
});

Vue.config.productionTip = false;

Vue.directive('click-outside', ELEMENT.utils.Clickoutside);
Vue.directive('scroll-shadow', ScrollShadow);

// Defer ResizeObserver callbacks to the next frame to avoid the benign
// "ResizeObserver loop completed with undelivered notifications" warning that
// surfaces in the webpack-dev-server overlay when ECharts autoresize triggers
// another layout before the browser paints.
(() => {
  const OriginalResizeObserver = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
  if (!OriginalResizeObserver) return;
  class DeferredResizeObserver extends OriginalResizeObserver {
    constructor(cb: ResizeObserverCallback) {
      let rafId: number | null = null;
      super((entries, observer) => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = null;
          try { cb(entries, observer); } catch { /* swallow */ }
        });
      });
    }
  }
  (window as unknown as { ResizeObserver: typeof ResizeObserver }).ResizeObserver = DeferredResizeObserver;
})();

Vue.component('VChart', ECharts);
Vue.component('VueSlider', VueSlider);
Vue.use(DialogCommons.plugin);
Vue.use(NotificationCommons.plugin);
Vue.use(PiniaVuePlugin);
Vue.use(VueRouter);
Vue.use(VTooltip);
Vue.use(vuescroll);
Vue.use(ELEMENT);
Vue.use(abilitiesPlugin, ability, {
  useGlobalProperties: true,
});
Vue.use(VueContentPlaceholders);
Vue.use<PluginConfig>(vueDebounce, { lock: true, defaultTime: '400ms', listenTo: 'keyup' });

new Vue({
  pinia,
  router,
  render: (h) => h(App),
  i18n,
}).$mount('#app');
